import React, { useMemo, useCallback, useEffect, useState, PropsWithChildren } from 'react';
import Container from '@mui/material/Container';
import debounce from 'lodash-es/debounce';
import flatten from 'lodash-es/flatten';
import uniqBy from 'lodash-es/uniqBy';
import { gql } from '@yi/core';
import { i18n_course_outline } from '@yi/i18n/phrases/i18n_course_outline';
import { i18n_faq } from '@yi/i18n/phrases/i18n_faq';
import { i18n_frequently_asked_questions } from '@yi/i18n/phrases/i18n_frequently_asked_questions';
import { i18n_outline } from '@yi/i18n/phrases/i18n_outline';
import { i18n_overview } from '@yi/i18n/phrases/i18n_overview';
import { i18n_teachers } from '@yi/i18n/phrases/i18n_teachers';

import { analytics, isNonEmptyString } from '~/common';
import { Outline, TabSection, Sections, ErrorBoundary, useLocale, TabBar } from '~/components';
import { getBrightcoveMediaId } from '~/src/common/media';
import { MediaPlayerModal, BasicPlayer, Autoplay } from '~/src/components/MediaPlayer';
import { courseSalesPageTabsZIndex } from '~/styles';
import { AnyTypename, isNotNull, SalesCourse, SalesCourseBundle } from '~/types';

import { AboutCourse } from './AboutCourse';
import { CourseBanner } from './CourseBanner';
import { CourseCTASnackbar } from './CourseCTASnackbar';
import { TabHeading, TabsWrapper } from './elements';
import { SetVideoPlayerType } from './SetVideoPlayerType';
import { FAQTab, TeachersTab } from './Tabs';
import { usePriceInfo } from './usePriceInfo';

type SalesPage = gql.typesSsr.Query_Course_yi_Course_sales_page_select;
interface BaseProps {
  location: Location;
  salesPage: AnyTypename<SalesPage>;
  courseWrapperRef: React.MutableRefObject<HTMLDivElement | null>;
  course?: SalesCourse;
  bundle?: SalesCourseBundle;
}
interface CourseProps extends BaseProps {
  course: SalesCourse;
}
interface CourseBundleProps extends BaseProps {
  bundle: SalesCourseBundle;
}
type Props = CourseProps | CourseBundleProps;

export const CourseSalesPage: React.FC<PropsWithChildren<Props>> = ({
  salesPage,
  bundle,
  course,
  courseWrapperRef,
}) => {
  const { locale } = useLocale();
  const [activeSection, setActiveSection] = useState('overview');
  const onClickTab = (tabName: string) => () => setActiveSection(tabName);
  const { caption_title, jwplayer_id, brightcove_id, buttons, banner, sections } = salesPage;
  const [showBanner, setShowBanner] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [snackbarClosed, setSnackbarClosed] = useState(false);
  const [jwPlayerId, setJwPlayerId] = useState('');

  useEffect(() => {
    if (course) {
      analytics.sendAddToCartEvent({ course });
    }

    if (bundle) {
      analytics.sendAddToCartEvent({ bundle });
    }
  }, [course, bundle]);

  // replacing useCallback with useMemo because the latest eslint-plugin-react-hooks version throws an error
  // solution reference: https://kyleshevlin.com/debounce-and-throttle-callbacks-with-react-hooks

  const handleScroll = useMemo(
    () =>
      debounce((e: Event) => {
        if (!e.target) return;
        const target = e.target;
        if (target instanceof HTMLElement && target.scrollTop > 500) {
          setShowBanner(true);
        }
      }, 50),
    [],
  );

  useEffect(() => {
    const node = courseWrapperRef.current;
    if (node && !snackbarClosed) {
      node.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (node) {
        node.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll, snackbarClosed, courseWrapperRef]);

  const setVideoPlayer: SetVideoPlayerType = useCallback(playerId => {
    setJwPlayerId(playerId);
    setShowVideoModal(true);
  }, []);

  const priceInfo = usePriceInfo({ course, bundle });
  if (!priceInfo) return null;

  const courses = bundle ? bundle.courses : course ? [course] : [];
  const teachers = bundle
    ? uniqBy(flatten(bundle.courses.map(c => c.teachers)), teacher => teacher.id)
    : course?.teachers || [];

  const tabSections: (Sections | null)[] = [
    {
      name: 'overview',
      displayName: i18n_overview(locale),
      render: ({ getSectionProps }) =>
        sections && (
          <div {...getSectionProps()}>
            <ErrorBoundary>
              <AboutCourse setVideoPlayer={setVideoPlayer} sections={sections.filter(isNotNull)} />
            </ErrorBoundary>
          </div>
        ),
    },
    {
      name: 'outline',
      displayName: i18n_outline(locale),
      render: ({ getSectionProps }) => (
        <div {...getSectionProps()} id="sales-page-outline">
          {courses.map(
            course_ =>
              course_.chapters && (
                <Container>
                  <TabsWrapper>
                    <TabHeading>
                      {bundle ? <>{course_.title}</> : i18n_course_outline(locale)}
                    </TabHeading>
                    <Outline units={course_.units} wide />
                  </TabsWrapper>
                </Container>
              ),
          )}
        </div>
      ),
    },
    salesPage.faq && {
      name: 'faq',
      displayName: i18n_faq(locale),
      render: ({ getSectionProps }) => (
        <Container>
          <TabsWrapper>
            <div {...getSectionProps()} id="sales-page-faq">
              <TabHeading>{i18n_frequently_asked_questions(locale)}</TabHeading>
              <FAQTab faq={salesPage?.faq} />
            </div>
          </TabsWrapper>
        </Container>
      ),
    },
    {
      name: 'teachers',
      displayName: i18n_teachers(locale),
      render: ({ getSectionProps }) => (
        <Container>
          <TabsWrapper>
            <div {...getSectionProps()} id="sales-page-teachers">
              <TabHeading>
                {i18n_teachers(locale)}
                <TeachersTab teachers={teachers} />
              </TabHeading>
            </div>
          </TabsWrapper>
        </Container>
      ),
    },
  ];

  const mediaId = getBrightcoveMediaId({ jwplayer_id: jwPlayerId, brightcove_id });
  const isValidBrightcoveMediaId = isNonEmptyString(mediaId);
  const showBrightcovePlayer = isValidBrightcoveMediaId && showVideoModal;

  return (
    <>
      <CourseBanner
        permissions={course?.permissions || null}
        salesPage={salesPage}
        priceInfo={priceInfo}
        showVideo={() => typeof jwplayer_id === 'string' && setVideoPlayer(jwplayer_id)}
        urlTitle={course?.url_title || bundle?.url_title || ''}
        courseType={bundle ? 'bundle' : 'ecourse'}
      />
      {banner && <div id="mobile-bg-url" style={{ display: 'none' }} data-url={banner.url} />}

      <TabBar
        sections={tabSections.filter(isNotNull)}
        activeSection={activeSection}
        onClickTab={onClickTab}
      />
      <TabSection
        sections={tabSections.filter(isNotNull)}
        activeSection={activeSection}
        onClickTab={onClickTab}
        containerStyles={{ margin: 0, padding: 0, maxWidth: '100%' }}
        sectionStyles={{ zIndex: courseSalesPageTabsZIndex }}
      />
      <CourseCTASnackbar
        title={caption_title || ''}
        showBanner={showBanner}
        setToggleBanner={open => {
          setShowBanner(open);
          setSnackbarClosed(!open);
        }}
        setClose={() => {
          setShowBanner(false);
          setSnackbarClosed(true);
        }}
        hasPreview={!!jwplayer_id}
        priceInfo={priceInfo}
        setVideoPlayer={() => typeof jwplayer_id === 'string' && setVideoPlayer(jwplayer_id)}
        buttons={buttons?.filter(isNotNull)}
        snackbarStyles={{ zIndex: courseSalesPageTabsZIndex }}
      />
      {showBrightcovePlayer && (
        <MediaPlayerModal open={showVideoModal} onClose={() => setShowVideoModal(false)}>
          <BasicPlayer mediaId={mediaId}>
            <Autoplay />
          </BasicPlayer>
        </MediaPlayerModal>
      )}
    </>
  );
};
